// styled.ts
import styled, { css } from 'styled-components';

// Import SVG icons
import iconApproved from '../../../../../assets/icons/courses/progressiveCertification/icon-header-approved.svg';
import iconFailed from '../../../../../assets/icons/courses/progressiveCertification/icon-header-failed.svg';
import iconInProgress from '../../../../../assets/icons/courses/progressiveCertification/icon-header-in-progress.svg';
import iconPending from '../../../../../assets/icons/courses/progressiveCertification/icon-header-pending.svg';

// Import new course state icons
import iconCourseApproved from '../../../../../assets/icons/courses/progressiveCertification/icon-course-approved.svg';
import iconCourseFailed from '../../../../../assets/icons/courses/progressiveCertification/icon-course-failed.svg';
import iconCourseInProgress from '../../../../../assets/icons/courses/progressiveCertification/icon-course-in-progress.svg';
import iconCoursePending from '../../../../../assets/icons/courses/progressiveCertification/icon-course-pending.svg';

// Constantes de colores para mantener consistencia
export const COLORS = {
  primary: '#5B36F2',
  success: '#007F38',
  error: '#B50D30',
  warning: '#906600',
  neutral: '#5B7B8E',
  background: '#d5e5f0',
  border: '#A8CBE2',
  text: '#161D1F',
  white: '#FFFFFF',
  black: '#000000'
} as const;

// Constantes de espaciado
export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px'
} as const;

export const Card = styled.div`
  width: 328;
  height: 305;
  border-radius: 8px;
  padding: 16px;
  gap: 16px;
  angle: 0 deg;
  opacity: 1;
  background-color: #ffffff;
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  cursor: pointer;

  .title {
    font-family: 'Lato';
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0px;
    color: #161d1f;
  }

  .icons {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const CardContent = styled.div`
  font-family: Lato;
  font-weight: 400;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 24px;
  letter-spacing: 0px;
  color: #161d1f;
  margin-top: 8px;
  .message {
    font-family: Lato;
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 0px;
  }
`;

export const DownloadButton = styled.button`
  width: 296;
  height: 44;
  gap: 8px;
  angle: 0 deg;
  opacity: 1;
  border-radius: 4px;
  padding-top: 10px;
  padding-right: 24px;
  padding-bottom: 10px;
  padding-left: 16px;
  background-color: #5b36f2;

  font-family: Lato;
  font-weight: 700;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;

  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const CourseGradesContainer = styled.div`
  width: 296;
  height: 94;
  gap: 8;
  angle: 0 deg;
  opacity: 1;
  border-radius: 8px;
  border-width: 1px;
  padding: 12px;
  gap: 8px;
  border: 1px solid #a8cbe2;
  margin-bottom: 16px;

  .course-name {
    font-family: Lato;
    font-weight: 400;
    font-size: 16px;
    leading-trim: NONE;
    line-height: 24px;
    letter-spacing: 0px;
    color: #161d1f;
  }
  .course-grade {
    font-family: Lato;
    font-weight: 400;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 0px;
    color: #161d1f;
  }
`;

export const StateIcon = styled.span<{ state: string }>`
  display: inline-flex;
  align-items: center;
  width: 32px;
  height: 32px;
  background-image: ${({ state }) => {
    switch (state) {
      case 'COMPLETED':
        return `url(${iconApproved})`;
      case 'FAILED':
        return `url(${iconFailed})`;
      case 'IN_PROGRESS':
        return `url(${iconInProgress})`;
      case 'NOT_ENROLLED':
        return `url(${iconPending})`;
      default:
        return `url(${iconPending})`;
    }
  }};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;
export const StateCourseIcon = styled.span<{ state: string }>`
  display: inline-flex;
  align-items: center;
  width: 16px;
  height: 16px;
  ${({ state }) =>
    state === 'PASSED' &&
    css`
      background-image: url(${iconCourseApproved});
    `}

  ${({ state }) =>
    state === 'FAILED' &&
    css`
      background-image: url(${iconCourseFailed});
    `}
    
    ${({ state }) =>
    state === 'IN_PROGRESS' &&
    css`
      background-image: url(${iconCourseInProgress});
    `}
    
    ${({ state }) =>
    state === 'NOT_EVALUATED' &&
    css`
      background-image: url(${iconCoursePending});
    `}
    
    background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

export const ArrowIcon = styled.span<{ isOpen: boolean }>`
  display: inline-block;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  &::before {
    content: '\\25BC';
    font-size: 12px;
    color: #a0aec0;
  }
`;

export const CertificationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Styled components para reemplazar estilos inline
export const FlexContainer = styled.div<{
  direction?: 'row' | 'column';
  align?: 'center' | 'flex-start' | 'flex-end';
  justify?: 'center' | 'flex-start' | 'flex-end' | 'space-between';
  gap?: keyof typeof SPACING;
}>`
  display: flex;
  flex-direction: ${(props) => props.direction || 'row'};
  align-items: ${(props) => props.align || 'center'};
  justify-content: ${(props) => props.justify || 'flex-start'};
  gap: ${(props) => (props.gap ? SPACING[props.gap] : '0')};
`;

export const IconContainer = styled.div`
  min-width: 25px;
  min-height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const WarningIcon = styled.img`
  width: 20px;
  height: 20px;
  max-width: none;
`;

export const ToggleButton = styled.button`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover {
    background-color: rgba(91, 54, 242, 0.1);
    border-radius: 4px;
  }

  &:focus {
    outline: 2px solid ${COLORS.primary};
    outline-offset: 2px;
  }
`;

export const CourseInfoContainer = styled.div`
  display: flex;
  align-items: center;
  font-family: Lato;
  font-weight: 400;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 0px;
  margin-top: 2px;
`;

export const CourseGrade = styled.span`
  /* Estilos específicos para la nota si es necesario */
`;

export const Divider = styled.div<{ orientation?: 'horizontal' | 'vertical' }>`
  background-color: ${COLORS.border};
  flex-shrink: 0;
  ${(props) =>
    props.orientation === 'vertical'
      ? `
    width: 1px;
    height: 20px;
    margin: 0 ${SPACING.lg};
  `
      : `
    width: 100%;
    height: 1px;
    margin: ${SPACING.sm} 0;
  `}
`;

export const CourseStateContainer = styled.span<{ state: string }>`
  color: ${(props) => {
    switch (props.state) {
      case 'PASSED':
        return COLORS.success;
      case 'FAILED':
        return COLORS.error;
      case 'NOT_EVALUATED':
        return COLORS.neutral;
      case 'IN_PROGRESS':
        return COLORS.warning;
      default:
        return COLORS.black;
    }
  }};
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
`;
